import { gqlRequest, setToken, clearToken, getToken } from './api';

export const authService = {
  async login(email, password) {
    const data = await gqlRequest(`
      mutation Login($email: String!, $password: String!) {
        login(email: $email, password: $password) {
          token
          user {
            id
            email
            role { id name code }
            userAttributes {
              id
              attribute { id name code }
              value
              userAttributeList {
                id
                items { id attribute { code } attributeGroup { code } value }
              }
            }
            buyerGroup {
              id
              name
              priceList { id name }
              paymentMethods { id code title methods { code title } }
            }
          }
        }
      }
    `, { email, password });

    const { token, user } = data.login;
    setToken(token);
    const transformed = transformUser(user);

    if (!user.buyerGroup) {
      try {
        await gqlRequest(`
          mutation UpdateUser($user: UserInputType!) {
            updateUser(user: $user) { id buyerGroup { id name paymentMethods { id code title methods { code title } } priceList { id name } } }
          }
        `, {
          user: { userAttributes: [{ attributeCode: 'buyer-group', value: JSON.stringify({ id: 40 }) }] }
        }, { requireAuth: true });
        const refreshed = await authService.getProfile();
        if (refreshed) return { token, user: refreshed };
      } catch (e) {
        console.warn('Buyer group auto-assign on login failed:', e.message);
      }
    }

    return { token, user: transformed };
  },

  async register({ email, password, roleCode, firstName, lastName, phone, dob, address, isB2B }) {
    const userAttributes = [
      { attributeCode: 'firstname', value: firstName },
      { attributeCode: 'lastname', value: lastName }
    ];

    if (phone) {
      userAttributes.push({ attributeCode: 'phone', value: phone });
    }
    if (dob) {
      userAttributes.push({ attributeCode: 'dob', value: dob });
    }

    const finalRoleCode = roleCode || (isB2B ? 'customer-company-admin' : 'customer-simple');

    const data = await gqlRequest(`
      mutation AddUser($user: UserInputType!, $forceChangePassword: Boolean, $createSlug: Boolean) {
        addUser(user: $user, forceChangePassword: $forceChangePassword, createSlug: $createSlug) {
          id
          email
          role { id name code }
          userAttributes { attribute { name code } value }
        }
      }
    `, {
      user: {
        email: email.toLowerCase(),
        password,
        roleCode: finalRoleCode,
        userAttributes
      },
      forceChangePassword: false,
      createSlug: false
    });

    await data.addUser;

    const loginResult = await authService.login(email.toLowerCase(), password);

    if (address) {
      try {
        await authService.saveAddress(address);
      } catch (e) {
        console.warn('Address save after registration failed:', e.message);
      }
    }

    return loginResult;
  },

  async getProfile() {
    const data = await gqlRequest(`
      query UserByToken {
        userByToken {
          id
          email
          role { id name code }
          userAttributes {
            id
            attribute { id name code }
            value
            userAttributeList {
              id
              items { id attribute { code } attributeGroup { code } value }
            }
          }
          buyerGroup {
            id
            name
            priceList { id name }
            paymentMethods { id code title methods { code title } }
          }
        }
      }
    `, {}, { requireAuth: true });

    return transformUser(data.userByToken);
  },

  async updateProfile({ email, userAttributes }) {
    const attrs = [];
    if (userAttributes) {
      for (const [code, value] of Object.entries(userAttributes)) {
        attrs.push({ attributeCode: code, value });
      }
    }

    const userInput = {};
    if (email) userInput.email = email;
    if (attrs.length > 0) userInput.userAttributes = attrs;

    const data = await gqlRequest(`
      mutation UpdateUser($user: UserInputType!, $roleCode: String) {
        updateUser(user: $user, roleCode: $roleCode) {
          id
          email
          role { id name code }
          userAttributes {
            id
            attribute { id name code }
            value
            userAttributeList {
              id
              items { id attribute { code } attributeGroup { code } value }
            }
          }
          buyerGroup {
            id
            name
            priceList { id name }
            paymentMethods { id code title methods { code title } }
          }
        }
      }
    `, { user: userInput }, { requireAuth: true });

    return transformUser(data.updateUser);
  },

  async changePassword(currentPassword, newPassword) {
    await gqlRequest(`
      mutation ChangePassword($currentPassword: String!, $newPassword: String!, $newPasswordConfirmation: String!) {
        changePassword(currentPassword: $currentPassword, newPassword: $newPassword, newPasswordConfirmation: $newPasswordConfirmation)
      }
    `, {
      currentPassword,
      newPassword,
      newPasswordConfirmation: newPassword
    }, { requireAuth: true });

    return true;
  },

  async saveAddress(address) {
    const data = await gqlRequest(`
      mutation SaveAddress($address: AddressInput!) {
        saveAddress(address: $address) {
          id
          street
          complement
          city
          state
          country
          postalCode
          defaultBilling
          defaultShipping
        }
      }
    `, {
      address: {
        street: address.street,
        complement: address.apt || address.complement || '',
        city: address.city,
        state: address.state,
        country: address.country || 'US',
        postalCode: address.zip || address.postalCode,
        defaultShipping: address.defaultShipping !== false,
        defaultBilling: address.defaultBilling !== false
      }
    }, { requireAuth: true });

    return data.saveAddress;
  },

  async checkEmailExists(email) {
    const data = await gqlRequest(`
      query UserEmailExists($email: String!) {
        userEmailExists(email: $email)
      }
    `, { email: email.toLowerCase() });

    return data.userEmailExists;
  },

  async forgotPassword(email) {
    await gqlRequest(`
      mutation ForgotPassword($email: String!) {
        forgotPassword(email: $email)
      }
    `, { email: email.toLowerCase() });
    return true;
  },

  logout() {
    clearToken();
  },

  isAuthenticated() {
    return !!getToken();
  }
};

function transformUser(apiUser) {
  if (!apiUser) return null;

  const attrs = {};
  const addressLists = [];

  if (apiUser.userAttributes) {
    for (const ua of apiUser.userAttributes) {
      const code = ua.attribute?.code || ua.attribute?.name;
      if (!code) continue;

      if (ua.userAttributeList && ua.userAttributeList.length > 0) {
        for (const list of ua.userAttributeList) {
          const addrObj = { id: list.id };
          if (list.items) {
            for (const item of list.items) {
              const itemCode = item.attribute?.code || item.attributeCode;
              addrObj[itemCode] = item.value;
            }
          }
          addressLists.push(addrObj);
        }
      } else {
        attrs[code] = ua.value;
      }
    }
  }

  const primaryAddress = addressLists[0] || null;
  const address = primaryAddress ? {
    street: primaryAddress['address-street'] || '',
    apt: primaryAddress['address-complement'] || '',
    city: primaryAddress['address-city'] || '',
    state: primaryAddress['address-region-code'] || primaryAddress['address-state'] || '',
    zip: primaryAddress['address-postcode'] || ''
  } : null;

  const isB2B = apiUser.role && [
    'customer-company-admin',
    'customer-company-manager',
    'customer-company-buyer'
  ].includes(apiUser.role.code);

  return {
    id: apiUser.id,
    email: apiUser.email,
    firstName: attrs.firstname || '',
    lastName: attrs.lastname || '',
    phone: attrs.phone || '',
    dob: attrs.dob || '',
    address,
    addresses: addressLists,
    insurance: attrs['insurance-provider'] ? {
      provider: attrs['insurance-provider'],
      memberId: attrs['insurance-member-id'],
      groupNumber: attrs['insurance-group-number']
    } : null,
    isB2B: isB2B || false,
    role: apiUser.role,
    buyerGroup: apiUser.buyerGroup,
    _raw: apiUser
  };
}
